// YNX BUSINESS HUB — onboarding (create or join workspace)
// ───────────────────────────────────────────────────────────────
import React, { useEffect, useState, useRef } from 'react';
import { Building2, KeyRound, Sparkles, ArrowLeft, CheckCircle2, Copy, ArrowRight, ShieldQuestion } from 'lucide-react';
import { createWorkspace, joinWorkspace, setLocalWorkspace } from '../lib/biz';
import { startGoogleLogin, claimAdmin, joinAsUser } from '../lib/auth';
import { track } from '../lib/tracker';
import { PreviewBanner } from './PreviewBanner';
import { OnboardRecoverTab } from './onboarding/OnboardRecoverTab';

export const BusinessOnboarding = ({ onReady, onBack, initialMode = 'join', currentUser, setCurrentUser }) => {
  const [mode, setMode] = useState(initialMode); // 'join' | 'create' | 'recover'
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [siret, setSiret] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [createdWs, setCreatedWs] = useState(null);  // full workspace once created
  const [copied, setCopied] = useState(false);
  const enteredRef = useRef(false);   // guard against double-enter

  useEffect(() => {
    track('onboard_start', { mode: initialMode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoin = async () => {
    if (!code.trim()) { setErr('Code requis.'); return; }
    // Normalize: strip ALL whitespace (handles "YNX - 2AHMRC" -> "YNX-2AHMRC")
    const c = code.replace(/\s+/g, '').toUpperCase();
    if (c === 'YNX-XXXXXX' || /^YNX-X+$/.test(c)) {
      setErr("Ne saisissez pas \"YNX-XXXXXX\" — c'est juste un exemple. Tapez le vrai code reçu de votre administrateur (ex: YNX-2AHMRC).");
      return;
    }
    if (!/^YNX-[A-Z0-9]{4,12}$/.test(c)) {
      setErr("Format invalide. Le code doit ressembler à YNX-2AHMRC (lettres + chiffres, sans espaces).");
      return;
    }
    setBusy(true); setErr('');
    try {
      // If logged in via Google, prefer join-as-user (registers membership).
      if (currentUser) {
        const result = await joinAsUser(c);
        setLocalWorkspace(result.workspace.id);
        track(result.workspace.is_demo ? 'demo_join' : 'signup', { mode: 'join-google', code: c });
        onReady(result.workspace);
        return;
      }
      const ws = await joinWorkspace(c);
      setLocalWorkspace(ws.id);
      track(ws.is_demo ? 'demo_join' : 'signup', { mode: 'join-anon', code: c });
      onReady(ws);
    } catch (e) {
      const msg = String(e?.message || '');
      track('onboard_join_error', { msg: msg.slice(0, 60) });
      if (msg === 'HTTP 404' || msg.includes('404') || msg.includes('invalide')) {
        setErr("Code d'accès introuvable. Si c'est votre première connexion sur ynxcloner.com, cliquez sur l'onglet 'Créer' à droite — votre code de production sera différent de celui de la preview.");
      } else {
        setErr('Connexion impossible : ' + msg);
      }
    }
    finally { setBusy(false); }
  };

  const handleCreate = async () => {
    if (name.trim().length < 2) { setErr('Nom d\'entreprise requis (2+ caractères).'); return; }
    setBusy(true); setErr('');
    try {
      const ws = await createWorkspace({ name: name.trim(), owner_email: email.trim(), siret: siret.trim() });
      setLocalWorkspace(ws.id);
      // If user is authenticated via Google, instantly claim the admin role
      // on the freshly created workspace. Silently ignored if it fails.
      if (currentUser) {
        try { await claimAdmin(ws.id); }
        catch (e) { console.debug('YNX onboard: claim-admin failed (non-blocking)', e); }
      }
      track('signup', { mode: 'create', plan: ws.plan });
      // Show the success screen with the access code. The user must explicitly
      // click "Entrer dans mon espace" — this is far more reliable than a
      // 4-second setTimeout whose closure may get garbage-collected if the
      // component re-renders while the timer is pending.
      setCreatedWs(ws);
    } catch (e) {
      track('onboard_create_error', { msg: String(e?.message || '').slice(0, 60) });
      setErr(e.message || 'Erreur inconnue lors de la création.');
    }
    finally { setBusy(false); }
  };

  const enterWorkspace = () => {
    if (enteredRef.current || !createdWs) return;
    enteredRef.current = true;
    onReady(createdWs);
  };

  const copyAccessCode = async () => {
    if (!createdWs?.access_code) return;
    try {
      await navigator.clipboard.writeText(createdWs.access_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.debug('YNX onboard: clipboard failed', e); }
  };

  // ─── Success screen — workspace just created ───────────
  if (createdWs) {
    return (
      <div className='biz-onboard-wrap'>
        <PreviewBanner />
        <div className='biz-onboard-card biz-onboard-success'>
          <div className='biz-onboard-success-icon'>
            <CheckCircle2 size={44} />
          </div>
          <h1 className='biz-onboard-title' style={{ marginBottom: 6 }}>Espace entreprise créé !</h1>
          <p className='biz-onboard-sub'>
            Bienvenue <b>{createdWs.name}</b> · Voici votre code d'accès personnel.<br/>
            Notez-le ou partagez-le avec vos collaborateurs.
          </p>
          <div
            className='biz-code-display'
            onClick={copyAccessCode}
            title='Cliquer pour copier'
            data-testid='biz-created-code'
          >
            {createdWs.access_code}
          </div>
          <button
            type='button'
            className='biz-btn-ghost sm'
            onClick={copyAccessCode}
            style={{ marginTop: 12, marginBottom: 24 }}
            data-testid='biz-copy-code-btn'
          >
            <Copy size={12} /> {copied ? '✓ Copié !' : 'Copier le code'}
          </button>
          <button
            type='button'
            className='biz-btn-primary'
            onClick={enterWorkspace}
            data-testid='biz-enter-workspace-btn'
            style={{ width: '100%' }}
            autoFocus
          >
            Entrer dans mon espace <ArrowRight size={14} />
          </button>
          <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 18, lineHeight: 1.55 }}>
            🔒 <b>Important :</b> conservez ce code en lieu sûr. Il vous permettra de retrouver
            votre espace depuis n'importe quel appareil.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='biz-onboard-wrap'>
      <PreviewBanner />
      <div className='biz-onboard-card'>
        {onBack && (
          <button
            type='button'
            onClick={onBack}
            disabled={busy}
            className='biz-onboard-back'
            data-testid='biz-back-landing'
          >
            <ArrowLeft size={14} /> Retour à l'accueil
          </button>
        )}
        <div className='biz-onboard-brand'>
          <span className='biz-onboard-logo'>YNX</span>
          <span className='biz-onboard-tag'>BY CARL TACITA</span>
        </div>
        <h1 className='biz-onboard-title'>L'OS IA des entreprises</h1>
        <p className='biz-onboard-sub'>Agenda · Devis · Factures · Tâches · Clients · Studio Visuel</p>

        {/* ─── Google Auth — preferred for multi-user workspaces ─── */}
        {currentUser ? (
          <div className='biz-google-connected' data-testid='biz-google-connected'>
            <Sparkles size={14} style={{ color: 'var(--cyan)' }} />
            <span>Connecté en tant que <b>{currentUser.email}</b></span>
          </div>
        ) : (
          <>
            <button
              type='button'
              className='biz-google-btn'
              onClick={startGoogleLogin}
              data-testid='biz-google-login'
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.9 6.1 29.7 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.9 6.1 29.7 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.6 0 10.7-2.1 14.5-5.7l-6.7-5.5C29.8 34.6 27 35.5 24 35.5c-5.1 0-9.4-3.3-11-7.9l-6.6 5.1C9.6 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.7 5.5c-.5.5 7.3-5.3 7.3-15.1 0-1.3-.1-2.3-.4-3.5z"/>
              </svg>
              Continuer avec Google
            </button>
            <div className='biz-divider'><span>OU UTILISEZ UN CODE D'ACCÈS</span></div>
          </>
        )}

        <div className='biz-onboard-tabs'>
          <button
            className={`biz-onboard-tab ${mode === 'join' ? 'active' : ''}`}
            onClick={() => { setMode('join'); setErr(''); }}
            data-testid='biz-tab-join'
          >
            <KeyRound size={16} /> Rejoindre
          </button>
          <button
            className={`biz-onboard-tab ${mode === 'create' ? 'active' : ''}`}
            onClick={() => { setMode('create'); setErr(''); }}
            data-testid='biz-tab-create'
          >
            <Building2 size={16} /> Créer
          </button>
          <button
            className={`biz-onboard-tab ${mode === 'recover' ? 'active' : ''}`}
            onClick={() => { setMode('recover'); setErr(''); }}
            data-testid='biz-tab-recover'
            title='Retrouver mon espace par e-mail'
          >
            <ShieldQuestion size={16} /> Retrouver
          </button>
        </div>

        {mode === 'join' && (
          <>
            <label className='biz-label'>Code d'accès entreprise (fourni par votre administrateur)</label>
            <input
              className='biz-input'
              name='ws-code'
              placeholder='Exemple : YNX-2AHMRC'
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
              autoFocus
              data-testid='biz-code-input'
            />
            <button
              className='biz-btn-primary'
              disabled={busy}
              onClick={handleJoin}
              data-testid='biz-join-btn'
            >
              {busy ? 'Connexion…' : 'Rejoindre l\'espace'}
            </button>
          </>
        )}

        {mode === 'create' && (
          <>
            <label className='biz-label'>Nom de l'entreprise *</label>
            <input
              className='biz-input'
              name='ws-name'
              placeholder='Ex: TACITA Conseil SARL'
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid='biz-name-input'
            />
            <label className='biz-label'>Email du gérant</label>
            <input
              className='biz-input'
              name='ws-email'
              type='email'
              placeholder='contact@entreprise.fr'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid='biz-email-input'
            />
            <label className='biz-label'>SIRET (optionnel)</label>
            <input
              className='biz-input'
              name='ws-siret'
              placeholder='12345678900012'
              value={siret}
              onChange={(e) => setSiret(e.target.value)}
              data-testid='biz-siret-input'
            />
            <button
              className='biz-btn-primary'
              disabled={busy}
              onClick={handleCreate}
              data-testid='biz-create-btn'
            >
              {busy ? 'Création…' : 'Créer mon espace entreprise'}
            </button>
          </>
        )}

        {mode === 'recover' && (
          <OnboardRecoverTab onReady={onReady} onError={setErr} />
        )}

        {err && <div className='biz-error'>⚠ {err}</div>}

        <div className='biz-onboard-footer'>
          Hébergé chez <b>Emergent</b> · données stockées en MongoDB · conforme RGPD
        </div>
      </div>
    </div>
  );
};
